import { useTranslation } from "react-i18next";
import { Color } from "config/colors";
import "react-tooltip/dist/react-tooltip.css";
import { Text, CheckInOutModal, GigInfoContainer } from "./clockStamp.styled";
import { FontFamily, FontSize } from "config/font";
import { Modal } from "@material-ui/core";
import moment from "moment-timezone";
import { TimeReportForStamping } from "model/Company";
import { useCallback } from "react";
import { ButtonStyled } from "components/buttons/buttons.styled";
import { CustomIcon } from "components/icon/customIcon.component";

type Props = {
  timeReport?: TimeReportForStamping;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  isAutoGeneratedGig: boolean;
  setIsAutoGeneratedGig: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitTimeReport: () => void;
  clearInputs: () => void;
};

export const ClockStampCheckInOutModal: React.FC<Props> = ({
  timeReport,
  setShowModal,
  setKeyShouldBeDisabled,
  showModal,
  isAutoGeneratedGig,
  setIsAutoGeneratedGig,
  handleSubmitTimeReport,
  clearInputs,
}) => {
  const { t } = useTranslation();

  const isCheckOut =
    timeReport?.checkInTime && !isAutoGeneratedGig && !timeReport?.checkOutTime;
  const userName = timeReport?.firstName;

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setKeyShouldBeDisabled(false);
    setIsAutoGeneratedGig(false);
    clearInputs();

  }, [setShowModal, setKeyShouldBeDisabled, setIsAutoGeneratedGig]);

  const formatTime = useCallback((time?: string | null) => {
    if (!time) return "—";
    return moment
      .tz(time, "UTC")
      .tz("Europe/Stockholm")
      .format("YYYY-MM-DD HH:mm");
  }, []);

  return (
    <Modal
      open={showModal}
      onClose={handleCloseModal}
      aria-labelledby="checkin-checkout-modal"
      aria-describedby="checkin-checkout-modal-description"
    >
      <CheckInOutModal>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <CustomIcon
            name="cross"
            size="35px"
            onClick={() => handleCloseModal()}
            color={Color.LighterDestructive}
            style={{ marginBottom: 10 }}
          />
        </div>
        <Text
          fontFamily={FontFamily.MontserratRegular}
          fontSize={FontSize.H3}
          color={Color.MidnightBlue}
        >
          {isCheckOut
            ? `${t("StampClock.HaveANiceDay")} ${userName}!`
            : `${t("StampClock.Welcome")} ${userName}!`}
        </Text>
        <Text
          fontFamily={FontFamily.MontserratRegular}
          fontSize={FontSize.H4}
          color={Color.MidnightBlue}
        >
          {isCheckOut
            ? t("StampClock.YouAreCheckedOut")
            : t("StampClock.YouAreCheckedIn")}
        </Text>
        {!isCheckOut && timeReport && (
          <GigInfoContainer>
            <Text
              fontFamily={FontFamily.MontserratRegular}
              fontSize={FontSize.Standard}
              color={Color.MidnightBlue}
            >
              {timeReport.gigRole}
            </Text>
            <Text
              fontFamily={FontFamily.MontserratRegular}
              fontSize={FontSize.Standard}
              color={Color.MidnightBlue}
            >
              {`${t("StampClock.StartTime")}: ${formatTime(
                timeReport.startTime
              )}`}
            </Text>
            <Text
              fontFamily={FontFamily.MontserratRegular}
              fontSize={FontSize.Standard}
              color={Color.MidnightBlue}
            >
              {`${t("StampClock.EndTime")}: ${formatTime(timeReport.endTime)}`}
            </Text>
          </GigInfoContainer>
        )}
        {isCheckOut && (
          <ButtonStyled
            backgroundColor={Color.ModernGreen}
            onClick={() => handleSubmitTimeReport()}
          >
            {t("CompanyTimeReport.IWantToTimeReport")}
          </ButtonStyled>
        )}
      </CheckInOutModal>
    </Modal>
  );
};
